import { useState, useMemo } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Phone, PhoneIncoming, PhoneOutgoing, User, Briefcase, Clock, CheckCircle, XCircle, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { useCallsList } from '@/hooks/useCallsList';

const CONTACT_TYPES = [
  { value: 'prospect', label: 'Prospect', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'active_customer', label: 'Cliente Ativo', color: 'bg-green-100 text-green-800' },
  { value: 'past_customer', label: 'Ex-Cliente', color: 'bg-blue-100 text-blue-800' },
];

export default function CallsListTable({ 
  dateRange, 
  selectedUserIds = [],
  direction = null 
}) {
  const navigate = useNavigate();
  const [contactTypeFilter, setContactTypeFilter] = useState('all');
  const [activeDealFilter, setActiveDealFilter] = useState('all');

  const filters = useMemo(() => ({
    startDate: dateRange?.from,
    endDate: dateRange?.to,
    userIds: selectedUserIds.length > 0 ? selectedUserIds : undefined,
    direction: direction,
    contactTypes: contactTypeFilter !== 'all' ? [contactTypeFilter] : undefined,
    hasActiveDeal: activeDealFilter === 'all' ? null : activeDealFilter === 'with_deal'
  }), [dateRange?.from, dateRange?.to, selectedUserIds, direction, contactTypeFilter, activeDealFilter]);

  const { calls, loading, totalCount, page, setPage, pageSize } = useCallsList(filters);

  const totalPages = Math.ceil(totalCount / pageSize);

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const getContactName = (contact) => {
    if (!contact) return 'Desconhecido';
    const firstName = contact.first_name || '';
    const lastName = contact.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'Sem nome';
  };


  const getContactTypeBadge = (contactType) => {
    const type = CONTACT_TYPES.find(t => t.value === contactType);
    if (!type) return <Badge variant="outline">-</Badge>;
    return <Badge className={type.color}>{type.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Completada</Badge>;
      case 'missed':
      case 'no-answer':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Perdida</Badge>;
      case 'scheduled':
        return <Badge className="bg-blue-100 text-blue-800"><Calendar className="w-3 h-3 mr-1" />Agendada</Badge>;
      case 'in-progress':
        return <Badge className="bg-yellow-100 text-yellow-800"><Phone className="w-3 h-3 mr-1" />Em andamento</Badge>;
      default:
        return <Badge variant="outline">{status || '-'}</Badge>;
    }
  };

  const getDirectionIcon = (dir) => {
    if (dir === 'inbound') {
      return <PhoneIncoming className="w-4 h-4 text-green-600" />;
    }
    return <PhoneOutgoing className="w-4 h-4 text-blue-600" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="w-5 h-5" />
          Lista de Chamadas
          <Badge variant="secondary" className="ml-2">{totalCount} total</Badge>
        </CardTitle>
        
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mt-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Tipo de Contato</span>
            <Select value={contactTypeFilter} onValueChange={setContactTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {CONTACT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Deal Ativo</span>
            <Select value={activeDealFilter} onValueChange={setActiveDealFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="with_deal">Com Deal Ativo</SelectItem>
                <SelectItem value="without_deal">Sem Deal Ativo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Data/Hora</TableHead>
                    <TableHead>Contato</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Deal Ativo</TableHead>
                    <TableHead className="w-[60px]">Dir.</TableHead>
                    <TableHead className="w-[80px]">Duração</TableHead>
                    <TableHead>Status</TableHead>
                    
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {calls.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        Nenhuma chamada encontrada com os filtros selecionados
                      </TableCell>
                    </TableRow>
                  ) : (
                    calls.map((call) => (
                      <TableRow key={call.id} className="cursor-pointer hover:bg-muted/50">
                        <TableCell className="text-sm">
                          {safeFormat(new Date(call.created_at), "dd/MM/yy HH:mm", { locale: ptBR })}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="link"
                            className="p-0 h-auto font-medium"
                            onClick={() => call.contact?.id && navigate(`/ContactDetail?id=${call.contact.id}`)}
                            disabled={!call.contact?.id}
                          >
                            <User className="w-3 h-3 mr-1" />
                            {getContactName(call.contact)}
                          </Button>
                          {(call.contact?.primary_phone || (!call.contact && (call.from_e164 || call.to_e164))) && (
                            <div className="text-xs text-muted-foreground">
                              {call.contact?.primary_phone || (call.direction === 'inbound' ? call.from_e164 : call.to_e164)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {getContactTypeBadge(call.contact?.contact_type)}
                        </TableCell>
                        <TableCell>
                          {call.hasActiveDeal ? (
                            <Button
                              variant="link"
                              className="p-0 h-auto text-green-600"
                              onClick={() => call.deal?.id && window.open(`/DealDetail?id=${call.deal.id}`, '_blank')}
                            >
                              <Briefcase className="w-3 h-3 mr-1" />
                              Sim
                            </Button>
                          ) : (
                            <span className="text-muted-foreground flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              Não
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {getDirectionIcon(call.direction)}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-muted-foreground" />
                            {formatDuration(call.duration_seconds)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(call.displayStatus || call.status)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex justify-center">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setPage(Math.max(1, page - 1))}
                        className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                    
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      return (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            onClick={() => setPage(pageNum)}
                            isActive={page === pageNum}
                            className="cursor-pointer"
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        className={page === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
